import { useState } from 'react';
import { isAxiosError } from 'axios';
import { ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { useT } from '@/contexts/I18nContext';
import {
  openAdminTunnel,
  type AdminTunnelCapability,
  type AdminTunnelKind,
} from '@/api/adminTunnel';
import { extractApiError } from '@/utils/apiError';

interface Props {
  kind: AdminTunnelKind;
  id: number | string;
  capability: AdminTunnelCapability | null | undefined;
  className?: string;
}

/**
 * Opens an on-demand SSH-forward through the jetson to the device's
 * web admin UI (issue #34). Render conditional on `capability` being
 * non-null — if the BE doesn't know an admin port for this device's
 * vendor+model, we render nothing (no button, no UI real estate).
 *
 * The tunnel-issuing BE endpoint
 * (`POST /equipment_discovery/<kind>/<id>/admin-tunnel/`) lands in a
 * follow-up commit; until then the click 404s and the operator sees a
 * clear "функция готовится" error.
 */
export function AdminTunnelButton({ kind, id, capability, className }: Props) {
  const { t } = useT();
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!capability) return null;

  const handleClick = async () => {
    setOpening(true);
    setError(null);
    try {
      const { data } = await openAdminTunnel(kind, id);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      if (isAxiosError(e)) {
        setError(extractApiError(e, t('equipment.adminTunnel.error')));
      } else {
        setError(t('equipment.adminTunnel.error'));
      }
      window.setTimeout(() => setError(null), 6000);
    } finally {
      setOpening(false);
    }
  };

  const tooltip = `${t('equipment.adminTunnel.tooltip')} (${capability.label} · :${capability.port})`;

  return (
    <div className={className}>
      <Tooltip content={tooltip}>
        <Button variant="outline" size="sm" onClick={handleClick} disabled={opening}>
          {opening ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <ExternalLink className="size-3.5" />
          )}
          {t('equipment.adminTunnel.open')}
        </Button>
      </Tooltip>
      {error && (
        <div className="mt-1 text-xs text-destructive">{error}</div>
      )}
    </div>
  );
}
